import {useCallback, useMemo} from 'react';
import {StyleSheet, TouchableOpacity, View, Dimensions} from 'react-native';
import Styles from '../../Styles';
import AVText from 'src/components/elements/AVText';
import {CampusLocationType} from 'src/types/CampusLocationTypes';
import {getDescriber as getRadioButtonDescrinber} from 'src/components/elements/orderAhead/descriptor/DescriptorType';
import {getDescriber} from 'src/components/elements/home/descriptors/campusComponents/DescriptorType';
import VendingMachineIcon from 'src/components/img/svg/VendingMachineIcon';
import CustomRadioButton from 'src/components/elements/CustomRadioButton';
import {getLayoutDescriber} from 'src/components/elements/layoutDescriptor/DescriptorType';

type VendingMachineViewProps = {
  location: CampusLocationType;
  isSelected: boolean;
  onLocationClick: (index: number) => void;
  index: number;
};

const width = Dimensions.get('window').width;

const {getScanAndPayTile} = getDescriber();
const {pickupLocationInnerCircleBackgroundColor} = getRadioButtonDescrinber();

const VendingMachineView = (props: VendingMachineViewProps) => {
  const renderIcon = useMemo(() => {
    return (
      <View
        style={[styles.iconView, getScanAndPayTile()['iconViewBackground']]}
      >
        <VendingMachineIcon />
      </View>
    );
  }, []);

  const renderTextContent = useMemo(() => {
    return (
      <View style={styles.titleView}>
        <AVText
          accessibilityLabel={`LocationName${
            props.location.displayName ?? props.location.name
          }`}
          accessibilityRole="text"
          aria-label={`${
            props.location.displayName ?? props.location.name
          }, text`}
          maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm8}
          style={styles.titleText}
        >
          {props.location.displayName ?? props.location.name}
        </AVText>
      </View>
    );
  }, []);

  const handleOnPress = useCallback(
    () => props.onLocationClick(props.index),
    [props.onLocationClick, props.index],
  );

  return (
    <TouchableOpacity
      style={[styles.container, getScanAndPayTile()['rowSpace']]}
      accessibilityHint={`Double tap to select ${
        props.location.displayName ?? props.location.name
      }`}
      accessibilityLabel={`Location${
        props.location.displayName ?? props.location.name
      }`}
      accessibilityRole="button"
      onPress={handleOnPress}
      accessible={true}
      aria-label={`Location${
        props.location.displayName ?? props.location.name
      }`}
    >
      {renderIcon}
      {renderTextContent}
      <View style={styles.arrowView}>
        <CustomRadioButton
          selected={props.isSelected}
          accessibilityLabel={props.location.displayName ?? props.location.name}
          aria-label={props.location.displayName ?? props.location.name}
          onPress={() => props.onLocationClick(props.index)}
          touchableAreaPaddingStyle={styles.touchableAreaPaddingStyle}
          outerCircleStyle={{
            ...styles.outerCircleStyle,
            ...(props.isSelected && {
              borderColor: pickupLocationInnerCircleBackgroundColor(),
            }),
          }}
          innerCircleStyle={styles.innerCircleStyle}
        />
      </View>
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  container: {
    marginHorizontal: Styles.Spacing.m3 + Styles.Spacing.m2,
    width: width - (Styles.Spacing.m3 + Styles.Spacing.m2) * 2,
    justifyContent: 'flex-start',
    alignItems: 'center',
    flexDirection: 'row',
    borderRadius: Styles.Spacing.m2,
    ...getLayoutDescriber().shadowStyles().shadow,
  },
  iconView: {
    borderRadius: Styles.Spacing.m2,
    justifyContent: 'center',
    alignItems: 'center',
  },
  titleView: {
    marginRight: Styles.Spacing.m6,
  },
  titleText: {
    justifyContent: 'center',
    fontWeight: '700',
    color: Styles.black,
    fontSize: getLayoutDescriber().bodyLargeFontSize(),
  },
  arrowView: {
    justifyContent: 'center',
    alignItems: 'center',
    right: Styles.Spacing.m3,
    position: 'absolute',
  },
  outerCircleStyle: {
    height: 29,
    width: 29,
    borderRadius: Styles.Heights.h5,
    borderWidth: 2,
    overflow: 'hidden',
  },
  innerCircleStyle: {
    height: 19,
    width: 19,
    borderRadius: Styles.Heights.h5,
    backgroundColor: pickupLocationInnerCircleBackgroundColor(),
    overflow: 'hidden',
  },
});

export default VendingMachineView;
